import createClient, { type Middleware } from 'openapi-fetch'

import type { paths } from './schema'

/**
 * The one door out of this application.
 *
 * Three things it does that a bare `fetch` does not, each of them a rule from
 * somewhere else in the platform:
 *
 * - **The API is this page's own origin.** Caddy serves the bundle and reverse
 *   proxies the routes to FastAPI on the same box, so the origin the page was
 *   loaded from is the origin the API answers on — in development, in the
 *   trailer and on the cloud node alike — and there is no CORS to configure
 *   and no base URL to get wrong per deployment.
 * - **Identity is attached here or nowhere.** In `trusted-header` mode the
 *   person goes on `X-SiteFocus-Person`; in `jwt` and `device` mode a bearer
 *   token goes on `Authorization`. No screen reads any of them, for the same
 *   reason no route does: a screen that knows how identity is carried is a
 *   screen that breaks when it changes.
 * - **Failures are remembered.** The last few API errors go into a ring
 *   buffer the feedback button sends with a report, so "it did not work" from
 *   a foreman arrives with the four requests that failed before he pressed the
 *   button. Nothing about the body is kept: a report is not a place to leak
 *   record content into a bug tracker.
 */

export type ApiError = {
  at: string
  method: string
  path: string
  status: number
  /** The API's own message key when it sent one; never the record content. */
  message_key?: string
}

const TRAIL_LIMIT = 8
const trail: ApiError[] = []

/** What went wrong lately, oldest first. Read by the feedback dialog. */
export function errorTrail(): readonly ApiError[] {
  return trail
}

export function clearErrorTrail(): void {
  trail.length = 0
}

function remember(error: ApiError): void {
  trail.push(error)
  if (trail.length > TRAIL_LIMIT) trail.shift()
}

/**
 * How this browser proves who it is.
 *
 * Three modes and not two, because the third is a genuinely different door and
 * naming it the same as the directory's would hide that. `jwt` is a person who
 * signed in from a laptop and carries the directory's own token; `device` is a
 * shared tablet in a trailer where somebody tapped their name and typed a PIN,
 * and the token is one box's own — minted by `/signin/device`, marked `sfd_`,
 * good for a shift. The instance resolves an `sfd_` token *before* it looks at
 * which identity provider it is configured for, so a device session works on a
 * box running either mode; calling it `jwt` because the header happens to
 * match would be a name that survives into the next build and a lie by then.
 */
export type Credentials =
  | { mode: 'trusted-header'; personId: string }
  | { mode: 'jwt'; token: string }
  | { mode: 'device'; token: string }
  /**
   * A session this instance issued to a browser, in exchange for an email
   * address and a password. Its own mode rather than `device` reworded: the two
   * carry the same header and are the same row on the box, and calling a
   * laptop's session a device's is the kind of name that survives into the next
   * build. It is also what decides which sign-out route ends it.
   */
  | { mode: 'password'; token: string }
  /**
   * A session this instance issued to a browser that came back from an
   * identity provider. The same row on the box as the one above and the same
   * header on the wire — the door is what differs, and it differs in the one
   * way a screen has to know about: there is no password here to change and
   * nothing to reset, so a screen offering either would be sending somebody to
   * an administrator who cannot help them.
   */
  | { mode: 'federated'; token: string }
  | null

let credentials: Credentials = null

export function setCredentials(next: Credentials): void {
  credentials = next
}

export function currentCredentials(): Credentials {
  return credentials
}

/**
 * The identity header this session sends, for the callers that cannot go
 * through the typed client.
 *
 * The activity stream is server-sent events, and the browser's own EventSource
 * cannot set a request header at all — so in trusted-header mode it arrives
 * unidentified and the hub answers 401. The feed reads the stream with `fetch`
 * instead and needs the same header the rest of the client sends, from the
 * same place, so the two cannot drift.
 *
 * The other two callers are the screens whose path is chosen at runtime rather
 * than written at a call site: the generated module page and the overview's
 * court card. This said "the one caller" while the module page carried its own
 * copy of the same four lines — which is how a rule with one home ends up with
 * three, and why the copy is gone rather than merely noted.
 */
export function identityHeaders(): Record<string, string> {
  if (credentials === null) return {}
  // Exhausted rather than tested for, and that is the repair rather than a
  // tidier way of writing it. A chain of `if`s has a way of falling through to
  // "send nothing", which is a request that reads as anonymous rather than as
  // broken — and `password` fell through exactly that way, silently, for as
  // long as the sign-in page existed. A mode added to `Credentials` and not
  // handled here now fails to compile on the `never` below.
  switch (credentials.mode) {
    case 'trusted-header':
      return { 'X-SiteFocus-Person': credentials.personId }
    case 'jwt':
    case 'device':
    case 'password':
    case 'federated':
      return { Authorization: `Bearer ${credentials.token}` }
    default: {
      const unhandled: never = credentials
      throw new Error(`no identity header for ${JSON.stringify(unhandled)}`)
    }
  }
}

/**
 * One field the instance would not take, named the way the request named it.
 *
 * `missing` is a required field left out or left empty; anything else the
 * validator said about a value is `invalid`. The validator's own sentence is
 * not kept: it is English written on the server, and a form says this in the
 * reader's language from the kind alone.
 */
export type FieldProblem = { field: string; kind: 'missing' | 'invalid' }

/**
 * The fields a request-validation refusal names, if that is what this is.
 *
 * FastAPI answers a body or a query that fails its schema with HTTP 422 and a
 * `detail` that is a list, one entry per field, each with the path to the
 * field under `loc`. Every form in this application goes through `unwrap`, and
 * this shape used to fall through `refusalOf` to nothing at all, so a form
 * saved with its required fields empty said "HTTP 422" and named none of them.
 */
function fieldProblemsOf(detail: unknown): FieldProblem[] | undefined {
  if (!Array.isArray(detail)) return undefined
  const problems: FieldProblem[] = []
  for (const entry of detail) {
    if (typeof entry !== 'object' || entry === null) continue
    const loc = (entry as { loc?: unknown }).loc
    if (!Array.isArray(loc)) continue
    // The last name in the path is the field; `body` and `query` say where it
    // was sent, and a list index says which item, which a form already shows.
    const field = [...loc]
      .reverse()
      .find(
        (part): part is string =>
          typeof part === 'string' && part !== 'body' && part !== 'query' && part !== 'path',
      )
    if (!field || problems.some((problem) => problem.field === field)) continue
    const type = (entry as { type?: unknown }).type
    problems.push({ field, kind: type === 'missing' ? 'missing' : 'invalid' })
  }
  return problems.length > 0 ? problems : undefined
}

function refusalOf(body: unknown): {
  key?: string
  args?: Record<string, string>
  fields?: FieldProblem[]
} {
  if (typeof body !== 'object' || body === null) return {}
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return { key: detail }
  const fields = fieldProblemsOf(detail)
  if (fields) return { fields }
  if (typeof detail === 'object' && detail !== null) {
    const key = (detail as { message_key?: unknown }).message_key
    if (typeof key !== 'string') return {}
    return { key, args: messageArgsOf(detail) }
  }
  return {}
}

/**
 * The values the refusal came with, if it came with any.
 *
 * The instance sends them under `message_args`, which is the shape the
 * platform's own renderer takes and the shape the handheld reads on a
 * conflict. They are read as strings here because they are going into a
 * sentence; anything that is not a string, a number or a boolean is left out
 * rather than rendered as `[object Object]` in front of somebody.
 */
function messageArgsOf(detail: object): Record<string, string> | undefined {
  const args: Record<string, string> = {}
  // A refusal that carries a written reason rather than named values sends it
  // as `detail`, beside the key: the document modules answer "this set cannot
  // be issued" with the sentence that says which two pages claim the same
  // number. It is an argument like any other and belongs in the sentence the
  // key renders, so a translation can read "The set was not issued, because
  // {detail}." rather than dropping the part a reviewer needs.
  const written = (detail as { detail?: unknown }).detail
  if (typeof written === 'string' && written) args.detail = written

  const raw = (detail as { message_args?: unknown }).message_args
  if (typeof raw !== 'object' || raw === null)
    return Object.keys(args).length > 0 ? args : undefined
  for (const [name, value] of Object.entries(raw)) {
    if (typeof value === 'string') args[name] = value
    else if (typeof value === 'number' || typeof value === 'boolean') args[name] = String(value)
  }
  return Object.keys(args).length > 0 ? args : undefined
}

const identity: Middleware = {
  async onRequest({ request }) {
    // Through `identityHeaders()` rather than a second copy of the same
    // branches, and this is the reason the copy is gone rather than merely
    // corrected. `password` was added to `Credentials` with the sign-in page,
    // and to `identityHeaders()`, and not here — so every typed call a browser
    // made after signing in went out with no identity on it at all. The
    // instance refuses a request carrying nothing, correctly, which is a
    // sign-in page that answers 200 and a next call that answers 401.
    //
    // Nothing was checking, because nothing else in the deployment goes through
    // this door: the seed and the smoke check are their own clients and set the
    // header themselves, so both proved the session worked while the browser
    // could not use one.
    for (const [name, value] of Object.entries(identityHeaders())) {
      request.headers.set(name, value)
    }
    return request
  },
  async onResponse({ request, response }) {
    if (!response.ok) {
      // Read a clone: the caller still needs the body, and a response body can
      // only be consumed once.
      let key: string | undefined
      try {
        key = refusalOf(await response.clone().json()).key
      } catch {
        key = undefined
      }
      remember({
        at: new Date().toISOString(),
        method: request.method,
        path: new URL(request.url).pathname,
        status: response.status,
        ...(key ? { message_key: key } : {}),
      })
    }
    return response
  },
}

/**
 * Taken from the page rather than configured, so there is nothing to set per
 * box. It is spelled out rather than left empty because a request has to carry
 * an absolute URL once it leaves the browser's own resolution — under a test
 * runner there is no document to resolve against.
 */
const origin = globalThis.location?.origin ?? ''

export const api = createClient<paths>({ baseUrl: origin, credentials: 'same-origin' })
api.use(identity)

/**
 * What a screen shows when a call fails.
 *
 * The API answers a refusal with a message key rather than a sentence, because
 * a sentence would be stored in English somewhere. The key is resolved against
 * the platform catalogue when the client knows it, and shown as the key when
 * it does not — which is ugly and honest, and better than "Something went
 * wrong".
 */
export class ApiCallError extends Error {
  readonly status: number
  readonly messageKey?: string
  /**
   * What the key needs to say the whole sentence.
   *
   * A refusal that names a tool, a state or a date sends those values beside
   * the key. Dropping them here is how "There is no tool called {tool} on this
   * site." reached a screen with the braces still in it: the key arrived and
   * the answer did not.
   */
  readonly messageArgs?: Record<string, string>
  /**
   * The fields a request-validation refusal named, so a form can say which
   * ones to fix rather than a status code.
   */
  readonly fields?: readonly FieldProblem[]

  constructor(
    status: number,
    messageKey?: string,
    messageArgs?: Record<string, string>,
    fields?: readonly FieldProblem[],
  ) {
    super(messageKey ?? `HTTP ${status}`)
    this.name = 'ApiCallError'
    this.status = status
    if (messageKey) this.messageKey = messageKey
    if (messageArgs) this.messageArgs = messageArgs
    if (fields && fields.length > 0) this.fields = fields
  }
}

/**
 * The error a failed response stands for, from its status and its body.
 *
 * For the callers that cannot use the typed client because their path is
 * chosen at runtime. The generated module page threw `HTTP 422` from a bare
 * `fetch` and never read the body, so the instance's own sentence for the
 * refusal never reached the screen.
 */
export function callErrorOf(status: number, body: unknown): ApiCallError {
  const refusal = refusalOf(body)
  return new ApiCallError(status, refusal.key, refusal.args, refusal.fields)
}

/**
 * Whether a failure is the instance refusing this credential.
 *
 * The one failure that means somebody has to sign in again, and it has to be
 * told apart from the rest by hand: to a browser, "your token is no longer
 * accepted" and "the container is restarting" both arrive as a query that did
 * not answer. Treating them alike is how a deploy signs everybody out and
 * tells them their session expired, which sends them looking for a credential
 * problem that does not exist.
 *
 * A transport failure is deliberately not one of these. `fetch` rejecting
 * carries no status at all, and a browser that cannot reach the instance has
 * learnt nothing about whether the instance would accept it.
 */
export function credentialRefused(error: unknown): boolean {
  return error instanceof ApiCallError && error.status === 401
}

/**
 * Whether the instance declined to show this to whoever asked.
 *
 * Told apart from a read that merely failed, because a screen has to do a
 * different thing with each. A container restarting, a proxy hiccup or a query
 * that timed out leaves what is already on screen true and a little older than
 * it looks, which is what the stale notice says. A refusal says the opposite:
 * the instance looked at who was asking and would not answer, so anything a
 * previous answer put on screen is no longer something this browser may show.
 *
 * Both statuses, and 401 deliberately among them. A credential the instance no
 * longer accepts is handled globally -- it ends at the sign-in page -- but it
 * gets here first, and a panel that went on drawing record titles until the
 * redirect landed would be doing the same thing 403 is being corrected for.
 *
 * A transport failure is not one of these: `fetch` rejecting carries no status,
 * and a browser that could not reach the instance has learnt nothing about what
 * it would have allowed. That is an outage, and the cached answer stands with
 * the notice that says so.
 */
export function accessRefused(error: unknown): boolean {
  return error instanceof ApiCallError && (error.status === 403 || error.status === 401)
}

/**
 * Whether the instance itself refused this request, before it wrote anything.
 *
 * The one failure a screen may read as "nothing was committed", and it has to
 * be recognised rather than inferred from the status alone. A refusal this
 * application wrote carries its own message key in the envelope, and the
 * request session rolls back on the exception that raises it -- so a 4xx with
 * a key is the instance saying it looked at this request and declined it.
 *
 * Everything else is deliberately not one. A 4xx from a gateway carries no key
 * and says nothing about whether the request reached the instance; a 5xx says
 * less than that; and `fetch` rejecting carries no status at all, so a browser
 * that could not reach the instance has learnt nothing about what it would
 * have done. Each of those leaves a write uncertain, which is the state the
 * recovery card exists for.
 *
 * It answers for *this* request and no other. A replay of a write that was
 * already committed can be refused on its second pass -- authorization runs
 * before the replay guard does -- so a refusal arriving after a retry is not
 * evidence about the attempt that went before it.
 */
export function instanceRefused(error: unknown): boolean {
  // A request-validation refusal counts as well: FastAPI answers it before the
  // route runs, so nothing was written, and its per-field list is a shape no
  // gateway sends.
  return (
    error instanceof ApiCallError &&
    error.status >= 400 &&
    error.status < 500 &&
    (error.messageKey !== undefined || error.fields !== undefined)
  )
}

/** Unwrap an openapi-fetch result, or throw something a screen can render. */
export function unwrap<T>(result: {
  data?: T
  error?: unknown
  response: Response
}): T {
  if (result.data !== undefined) return result.data
  throw callErrorOf(result.response.status, result.error)
}

/**
 * A client-generated key for a write, so replaying it is a no-op.
 *
 * Every write route in this application takes one, because a handheld's
 * outbox and a foreman's double tap are the same problem. The browser is not
 * offline-capable in this build, but it is on a site uplink: a request that
 * times out and gets retried must not raise the deficiency twice.
 */
export function idempotencyKey(prefix: string): string {
  const random =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `${prefix}:${random}`
}
